import { usePriorityOptions } from "@features/ax/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { useEffect, useMemo, useState } from "react";
import {
  useAddServiceOrder,
  useOperationStatus,
  useSOPSelection,
} from "../fieldAlerts/failure/hooks";

interface CreateServiceOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void> | void;
  failureId?: string;
  incidentId?: string;
  requestDetails?: any;
}

export function CreateServiceOrderModal({
  isOpen,
  onClose,
  onSubmit,
  failureId,
  incidentId,
  requestDetails,
}: CreateServiceOrderModalProps) {
  const remoteUtilityId = useRemoteUtilityId();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: operationStatusData } = useOperationStatus();
  const { data: priorityData } = usePriorityOptions();
  const addServiceOrderMutation = useAddServiceOrder({
    failureId: failureId,
    incidentId: incidentId,
  });

  const priorityOptions =
    priorityData?.map((item: any) => ({
      value: item.key,
      label: item.value,
    })) || [];

  const operationStatusOptions =
    operationStatusData?.result?.map((item: any) => ({
      value: item.key,
      label: item.value,
    })) || [];

  const form = FormService.createForm({
    fields: [],
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      operationType: "",
      priority: "",
      sop: "",
      description: "",
    },
  });

  const selectedOperationType = form.watch("operationType");

  const { data: sopData } = useSOPSelection({
    remote_utility_id: remoteUtilityId,
    reading_source: "AX",
    association_type: "2",
    operation_type: selectedOperationType,
  });

  const sopSelectionOptions = useMemo(
    () =>
      sopData?.results?.map((item: any) => ({
        value: item.id,
        label: `${item.serviceNo} - ${item.name}`,
      })) || [],
    [sopData]
  );

  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "operationType",
        label: "Operation Type",
        type: "select",
        required: true,
        placeholder: "Select operation type",
        helperText: "Choose the type of operation for this service order",
        options: operationStatusOptions,
      },
      {
        name: "priority",
        label: "Priority",
        type: "select",
        required: true,
        placeholder: "Select priority level",
        helperText: "Choose the urgency level for this service order",
        options: priorityOptions,
      },
      {
        name: "sop",
        label: "Standard Operating Procedures (SOPs) Selection",
        type: "select",
        required: true,
        placeholder: "Select SOP",
        helperText: "Choose the applicable standard operating procedure",
        options: sopSelectionOptions,
        disabled: !selectedOperationType,
      },
    ],
    [
      operationStatusOptions,
      priorityOptions,
      sopSelectionOptions,
      selectedOperationType,
    ]
  );

  useEffect(() => {
    if (isOpen) {
      form.reset({
        operationType: "",
        priority: "",
        sop: "",
        description: "",
      });
    }
  }, [isOpen, form]);

  const handleSubmit = async (data: any) => {
    const formValues = form.getValues();
    const actualData = Object.keys(data).length === 0 ? formValues : data;

    const missingFields = [];
    if (!actualData.operationType) missingFields.push("Operation Type");
    if (!actualData.priority) missingFields.push("Priority");
    if (!actualData.sop) missingFields.push("SOP");

    // if (missingFields.length > 0) {
    //   const message = `Please fill in the following required fields: ${missingFields.join(", ")}`;
    //   alert(message);
    //   return;
    // }

    setIsSubmitting(true);
    try {
      let apiPayload;

      if (failureId) {
        apiPayload = {
          sop: actualData.sop,
          remote_utility_id: remoteUtilityId,
          request_id: requestDetails?.asset?.id,
          request_username: requestDetails?.asset?.assetName,
          request_account_no: requestDetails?.asset?.assetNo,
          consumer_address: requestDetails?.asset?.location,
          priority: actualData.priority,
          additional_data: {
            failure_id: failureId,
          },
        };
      } else if (incidentId) {
        const asset = requestDetails.asset;
        const assetName = asset?.assetName;
        const assetNo = asset?.assetNo;

        apiPayload = {
          sop: actualData.sop,
          remote_utility_id: remoteUtilityId,
          request_id: asset.id,
          request_username: assetName,
          request_account_no: assetNo,
          consumer_address: requestDetails.location || "N/A",
          priority: actualData.priority,
          additional_data: {
            incident_id: incidentId,
          },
        };
      }

      await addServiceOrderMutation.mutateAsync(apiPayload);

      const payload = {
        ...actualData,
        ...(failureId && { linkedFailureId: failureId }),
        ...(incidentId && { linkedIncidentId: incidentId }),
        createdAt: new Date().toISOString(),
      };

      await onSubmit(payload);
      form.reset();
      onClose();
      const incidentKey =
        QueryKeyFactory.module.ax.incidents.incidentDetail(incidentId);
      const failureKey = QueryKeyFactory.module.ax.failures.failuresDetails({
        remote_utility_id: remoteUtilityId,
        id: failureId,
      });
      if (incidentId) {
        queryClient.refetchQueries({
          queryKey: incidentKey,
        });
      } else {
        queryClient.refetchQueries({
          queryKey: failureKey,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.reset();
    onClose();
  };

  const handleCloseDialog = () => {
    form.reset();
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleCloseDialog}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Create Service Order</DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto max-h-[calc(90vh-200px)] px-1">
          <div className="space-y-6">
            {!operationStatusData && (
              <div className="text-sm text-gray-500">
                Loading operation types...
              </div>
            )}
            {!priorityData && (
              <div className="text-sm text-gray-500">Loading priorities...</div>
            )}

            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleCancel}
              gridColumns={2}
              config={{
                showSubmitButton: false,
                showResetButton: false,
                layout: "vertical",
                spacing: "comfortable",
                classes: FIELD_CLASSES,
              }}
              renderCustomFooter={() => (
                <div className="flex items-center justify-end gap-3 pt-6 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={form.handleSubmit(handleSubmit)}
                    disabled={isSubmitting}
                  >
                    Create Service Order
                  </Button>
                </div>
              )}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
